/**
 * 审计日志页：读留痕事件的中文标签与筛选提示。
 *
 * 常量与 admin/service/src/middleware/audit-read.ts 的 PII_READ_AUDIT / ADVISOR_AUDIT 一一对应
 * （两个包各有一份，改一边要同步另一边）。
 *
 * 后端 audit-logs 路由与仓储对 resourceType / action 没有白名单：值原样入表、原样按相等过滤，
 * 所以这里只管展示与筛选提示。未登记的值（写动作按路径推断出的 admin / refund / voucher /
 * create / update …）一律显示原值，不会因为没有标签而丢信息。
 */

export interface LabelOption {
  value: string;
  label: string;
}

/** 读留痕涉及的资源类型 → 中文。 */
export const RESOURCE_TYPE_LABELS: Readonly<Record<string, string>> = {
  user: '用户',
  order: '订单',
  partner: '合伙人',
  ticket: '工单',
  // 顾问陪跑里已有的读留痕（audit-read 的 ADVISOR_AUDIT）
  advisor_customer: '顾问客户',
  work: '作品',
};

/**
 * 读留痕动作 → 中文。
 * view_pii / view_pii_list 是后台拿到客户 / 合伙人联系方式明文（user / order / partner / ticket）；
 * 其余三个是顾问陪跑里销售查看客户讲述内容的既有读留痕。
 */
export const READ_ACTION_LABELS: Readonly<Record<string, string>> = {
  view_pii: '查看明文（详情）',
  view_pii_list: '查看明文（列表）',
  view_content: '查看讲述内容',
  view_pdf: '查看 PDF',
  view_progress: '查看进度',
};

/** 已登记的中文标签；未登记返回 null（调用方回退显示原值）。 */
export function resourceTypeLabel(resourceType: string): string | null {
  return Object.prototype.hasOwnProperty.call(RESOURCE_TYPE_LABELS, resourceType)
    ? RESOURCE_TYPE_LABELS[resourceType]
    : null;
}

export function actionLabel(action: string): string | null {
  return Object.prototype.hasOwnProperty.call(READ_ACTION_LABELS, action)
    ? READ_ACTION_LABELS[action]
    : null;
}

/** 该动作是否是读留痕事件（用来着色 / 在详情里加说明）。 */
export function isReadAuditAction(action: string): boolean {
  return actionLabel(action) !== null;
}

/** 「中文 · raw」：已登记的显示中文并保留原值，方便按原值筛选；未登记只显示原值。 */
export function withRaw(raw: string, label: string | null): string {
  return label ? `${label} · ${raw}` : raw;
}

function toOptions(labels: Readonly<Record<string, string>>): LabelOption[] {
  return Object.entries(labels).map(([value, label]) => ({ value, label: withRaw(value, label) }));
}

/** 资源类型筛选框的候选（仍可手输任意值）。 */
export const RESOURCE_TYPE_OPTIONS: LabelOption[] = toOptions(RESOURCE_TYPE_LABELS);

/** 动作筛选框的候选（仍可手输任意值）。 */
export const ACTION_OPTIONS: LabelOption[] = toOptions(READ_ACTION_LABELS);

/** 输入框边打字边筛候选：同时匹配原值与中文。 */
export function filterLabelOption(input: string, option?: LabelOption): boolean {
  if (!option) return false;
  const needle = input.trim().toLowerCase();
  return option.value.toLowerCase().includes(needle) || option.label.toLowerCase().includes(needle);
}
